import { memo, useMemo } from "react"
import { Skeleton } from "./Skeleton"

interface TableRowSkeletonProps {
	/** Number of columns to render */
	columns?: number
}

// Pre-generated stable keys for skeleton columns (max 10 columns)
const COLUMN_KEYS = Array.from({ length: 10 }, () => crypto.randomUUID())

// Pre-generated stable keys for skeleton rows (max 20 rows)
const ROW_KEYS = Array.from({ length: 20 }, () => crypto.randomUUID())

/**
 * Skeleton for table rows in admin UI.
 * Renders a row with configurable number of skeleton columns.
 * Height matches typical ~48px admin table row.
 */
export const TableRowSkeleton = memo(function TableRowSkeleton({
	columns = 4,
}: TableRowSkeletonProps) {
	const columnKeys = useMemo(() => COLUMN_KEYS.slice(0, columns), [columns])
	return (
		<tr className="border-gray-800 border-b">
			{columnKeys.map(key => (
				<td className="px-4 py-3" key={key}>
					<Skeleton className="h-4 w-full max-w-[120px]" />
				</td>
			))}
		</tr>
	)
})

interface TableSkeletonProps {
	/** Number of rows to render */
	rows?: number
	/** Number of columns per row */
	columns?: number
}

/**
 * Full table skeleton with multiple rows.
 * Use inside a table body element.
 */
export const TableSkeleton = memo(function TableSkeleton({
	rows = 5,
	columns = 4,
}: TableSkeletonProps) {
	const rowKeys = useMemo(() => ROW_KEYS.slice(0, rows), [rows])
	return (
		<>
			{rowKeys.map(key => (
				<TableRowSkeleton columns={columns} key={key} />
			))}
		</>
	)
})
